import MixpanelPlugin from '@satellite/plugins/MixpanelPlugin.js';
import type { AnalyticsClient } from '@satellite/analytics/AnalyticsClient';
import {
  attemptSessionReplayStart,
  createPathPrefixMatcher,
  createSessionReplayGate,
  exposeSessionReplayDebugHandle
} from '@satellite/analytics/sessionReplay';
import type {
  SessionReplayGate,
  SessionReplayGateContext
} from '@satellite/analytics/sessionReplay';

/** `window` key for the read-only Session Replay QA handle. */
const SESSION_REPLAY_DEBUG_HANDLE_NAME = '__odSessionReplay';

/**
 * Sampled Session Replay for apps that record whole sessions rather than a bounded funnel.
 *
 * Configuring this is what makes `client.maybeStartSessionRecording()` do something; without
 * it the app can still drive the recording by hand through the lifecycle methods.
 */
export interface SessionReplayGateConfig {
  /** sessionStorage key holding this app's sampling decision. One per app. */
  storageKey: string;
  /** Only paths under this prefix are recorded, e.g. `'/yard'`. */
  pathPrefix: string;
  /** App-level switch, ANDed with the presence of a Mixpanel token. */
  enabled: boolean;
  /** Percentage of eligible sessions to record, 0–100. */
  samplePercent: number;
}

export interface SessionReplayConfig {
  recordMaxMs: number;
  recordIdleTimeoutMs: number;
  /** Omit to keep the recording fully manual (Luna's registration funnel). */
  gate?: SessionReplayGateConfig;
}

export interface MixpanelAnalyticsClientOptions {
  /** Absent in development, QA and e2e — the whole client then no-ops. */
  token?: string | null;
  /**
   * Drives every environment-dependent default: SDK debug logging (`development`), gate
   * debug logging and the debug handle's `forceStart` (both non-`production`).
   */
  serverEnv?: string | null;
  /** Overrides the `serverEnv`-derived SDK debug logging. */
  debug?: boolean;
  /**
   * Opts the app into Session Replay. Passing this alone only configures the SDK; add
   * `gate` for sampled recording, or drive it by hand from the lifecycle methods.
   */
  sessionReplay?: SessionReplayConfig;
}

/**
 * Builds the analytics client every app provides through `analyticsKey`.
 *
 * This is the single implementation of `AnalyticsClient`. Apps supply their token, their
 * environment and — when they record sessions — their gate policy; they do not reimplement
 * the contract, and they do not hold the `MixpanelPlugin` instance. Anything an app needs
 * from Mixpanel belongs on `AnalyticsClient` so it stays reachable through `useAnalytics()`.
 *
 * Without a token the plugin never initializes and every method below no-ops, which is what
 * makes the client safe to create unconditionally in development, QA and e2e.
 */
export function createMixpanelAnalyticsClient({
  token = null,
  serverEnv = null,
  debug,
  sessionReplay
}: MixpanelAnalyticsClientOptions = {}): AnalyticsClient {
  const isProduction = serverEnv === 'production';

  const mixpanel = new MixpanelPlugin({
    token,
    enabled: Boolean(token),
    debug: debug ?? serverEnv === 'development'
  });

  mixpanel.init(
    sessionReplay
      ? {
          sessionReplay: {
            recordMaxMs: sessionReplay.recordMaxMs,
            recordIdleTimeoutMs: sessionReplay.recordIdleTimeoutMs
          }
        }
      : {}
  );

  const gateConfig = sessionReplay?.gate;
  let gate: SessionReplayGate | null = null;

  if (gateConfig) {
    gate = createSessionReplayGate({
      storageKey: gateConfig.storageKey,
      // The token check lives here rather than in every app's gate config: no token means
      // the plugin never initialized, so a start could only ever be a no-op.
      enabled: () => Boolean(token) && gateConfig.enabled,
      samplePercent: () => gateConfig.samplePercent,
      isEligiblePath: createPathPrefixMatcher(gateConfig.pathPrefix),
      debug: !isProduction
    });

    /**
     * Not optional polish — `mixpanel-browser` imported as a module does not set
     * `window.mixpanel`, so this handle is the only way to inspect gate state or read the
     * replay id during staging QA.
     */
    exposeSessionReplayDebugHandle(SESSION_REPLAY_DEBUG_HANDLE_NAME, {
      gate,
      recorder: mixpanel,
      allowForceStart: !isProduction
    });
  }

  return {
    // No-payload events arrive here without a second argument. `{}` is what the plugin
    // defaults to anyway, and what every app sent before this factory existed.
    track: (event, payload) => mixpanel.track(String(event), payload ?? {}),
    // Validation lives in the plugin, which reports a bad id when debug is on. Guarding
    // here too would swallow that signal before it could be logged.
    identify: distinctId => mixpanel.identify(distinctId),
    reset: () => mixpanel.reset(),
    registerSuper: properties => mixpanel.setProperties(properties),
    setUserProperties: properties => mixpanel.setUserProperties(properties),
    setGroupProperties: (groupKey, groupId, properties) =>
      mixpanel.setGroupProperties(groupKey, groupId, properties),

    startSessionRecording: () => mixpanel.startSessionRecording(),
    stopSessionRecording: () => mixpanel.stopSessionRecording(),
    pauseSessionRecording: () => mixpanel.pauseSessionRecording(),
    resumeSessionRecording: () => mixpanel.resumeSessionRecording(),

    maybeStartSessionRecording: (context: SessionReplayGateContext) => {
      if (!gate) {
        return;
      }
      /**
       * Fire-and-forget, and it swallows its own failures: the call site is a router
       * `afterEach` hook, which must not await analytics or surface its errors.
       */
      attemptSessionReplayStart({ gate, recorder: mixpanel, context });
    }
  };
}
